import React, { useState } from 'react';
import { AlertCircle, Mail, X } from 'lucide-react';
import { resendVerificationEmail } from '../../services/auth';
import type { User } from 'firebase/auth';

interface EmailVerificationAlertProps {
  user: User;
  onClose: () => void;
}

const EmailVerificationAlert: React.FC<EmailVerificationAlertProps> = ({ user, onClose }) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleResend = async () => {
    try {
      setSending(true);
      setError(null);
      await resendVerificationEmail(user);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white dark:bg-dark-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-500" />
            </div>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Verify your email
            </h3>
          </div>
        </div>

        <div className="mt-2">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            We've sent a verification email to <strong>{user.email}</strong>. Please check your inbox and click the verification link to activate your account.
          </p>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mt-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 p-3 rounded-md text-sm">
            Verification email sent successfully!
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 rounded-md"
          >
            Close
          </button>
          <button
            onClick={handleResend}
            disabled={sending || success}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail className="w-4 h-4 mr-2" />
            {sending ? 'Sending...' : 'Resend Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationAlert;